import { defineSmartFeedPiniaStore } from "@bloglovin/vue-framework";
import { useFanPublicationStore } from "%/stores/fan-publication-store";
import SmartFeedEngine from "~/smart-feed/smart-feed-engine";

const ENTITY_TYPE_COMMUNITY_POST = "communityPost";
const VIEWER_STATE_VISIBLE = "visible";

export const useMemberPostFeedStore = defineSmartFeedPiniaStore("member-post-feed-store", {
  state: () => ({
    smartFeedEngine: new SmartFeedEngine(),
    metadata: {
      lastId: Number.MAX_SAFE_INTEGER,
      publicationId: useFanPublicationStore().publicationId,
      viewerStates: {},
    },
    reportedStoryIds: [],
    pendingActionFor: 0,
    actionError: "",
  }),
  getters: {
    feedUrl() {
      return `/publication/${this.metadata.publicationId}/member-posts/smart-feed`;
    },
    memberPostEntities() {
      return SmartFeedEngine.getEntityPiniaStore(ENTITY_TYPE_COMMUNITY_POST)().entities;
    },
    visibleMemberPosts() {
      const entities = this.memberPostEntities;
      const viewerStates = this.metadata.viewerStates ?? {};

      return this.feed
          .map(feedItem => {
            const entity = entities[feedItem.id];
            if (!entity) {
              return null;
            }
            const state = viewerStates[feedItem.id] ?? {};
            return {
              ...entity,
              viewerState: state.viewerState ?? VIEWER_STATE_VISIBLE,
              isOwnPost: Boolean(state.isOwnPost),
            };
          })
          .filter(Boolean);
    },
    hasMoreMemberPosts() {
      return this.hasMore;
    },
  },
  actions: {
    toggleLove(post) {
      const publicationId = this.metadata.publicationId;
      const action = post.likeStatus ? "unlove" : "love";
      this.pendingActionFor = post.storyId;
      this.apiPost(
        `/publication/${publicationId}/member-posts/${post.storyId}/${action}`,
        {},
        () => {
          this.pendingActionFor = 0;
          const entity = this.memberPostEntities[post.storyId];
          if (!entity) {
            return;
          }
          entity.likeStatus = !entity.likeStatus;
          entity.numLikes = Math.max(0, (entity.numLikes || 0) + (entity.likeStatus ? 1 : -1));
        },
        () => {
          this.pendingActionFor = 0;
          this.actionError = "That did not save. Try again.";
        },
      );
    },
    reportPost(post, reason) {
      const publicationId = this.metadata.publicationId;
      this.pendingActionFor = post.storyId;
      this.apiPost(
        `/publication/${publicationId}/member-posts/${post.storyId}/report`,
        { reason },
        response => {
          this.pendingActionFor = 0;
          this.reportedStoryIds = [...this.reportedStoryIds, post.storyId];
          const entity = this.memberPostEntities[post.storyId];
          if (entity) {
            entity.numReports = response?.result?.num_reports ?? entity.numReports;
          }
        },
        () => {
          this.pendingActionFor = 0;
          this.actionError = "We could not send that report. Try again.";
        },
      );
    },
    deleteOwnPost(post) {
      const publicationId = this.metadata.publicationId;
      this.pendingActionFor = post.storyId;
      this.apiPost(
        `/publication/${publicationId}/member-posts/${post.storyId}/delete`,
        {},
        () => {
          this.pendingActionFor = 0;
          this.feed = this.feed.filter(feedItem => feedItem.id !== post.storyId);
        },
        () => {
          this.pendingActionFor = 0;
          this.actionError = "We could not delete that post. Try again.";
        },
      );
    },
    dismissActionError() {
      this.actionError = "";
    },
    hasBeenReported(storyId) {
      return this.reportedStoryIds.includes(storyId);
    },
    reloadMemberPosts() {
      this.feed = [];
      this.metadata = { ...this.metadata, lastId: Number.MAX_SAFE_INTEGER, hasMore: true };
      this.getSmartFeed();
    },
  },
});
